/**
 * ComponentOverrides — runtime persistence for component property tweaks.
 * Follows the same localStorage delta-from-defaults pattern as GodMode.ts.
 *
 * Key format: "${ComponentType}_${evolutionLevel}" e.g. "cannonLight_0"
 * Only non-default values are stored.
 */

import { ComponentType, COMPONENT_DEFS, ComponentDef, ComponentCategory, getEvolutionMultiplier } from '@/components/ComponentDefs';
import { EVOLUTION_MAP } from '@/config/ComponentEvolution';
import { notifyProfileChanged } from '@/config/ProfileSync';
import { GOD } from '@/config/GodMode';

// ── Overridable fields of ComponentDef (all numeric stats + edges + visual) ──

export interface ComponentOverride {
  // Connectivity
  allowedEdges?: [boolean, boolean, boolean, boolean];
  forbiddenEdges?: [boolean, boolean, boolean, boolean];
  // Economy
  cost?: number;
  unlockCost?: number;
  // Core stats
  hp?: number;
  mass?: number;
  energy?: number;
  // Movement
  force?: number;
  // Sensor
  sensorRange?: number;
  // Weapon
  damage?: number;
  fireRate?: number;
  weaponRange?: number;
  projectileSpeed?: number;
  // Explosive
  explosionDamage?: number;
  explosionRadius?: number;
  // Support
  repairRate?: number;
  rechargeRate?: number;
  supportRange?: number;
  cooldown?: number;
  // Visual
  offsetX?: number;
  offsetY?: number;
  defaultRotation?: number;
  scaleX?: number;
  scaleY?: number;
  flipX?: boolean;
  flipY?: boolean;
  // Grid click mode: 'rotate' = 90° rotation, 'flip' = cycle flipX → flipY → normal
  gridClickMode?: 'rotate' | 'flip';
}

/** All numeric override fields (used for delta detection + apply/restore) */
const NUMERIC_FIELDS: (keyof ComponentOverride)[] = [
  'cost', 'unlockCost', 'hp', 'mass', 'energy',
  'force', 'sensorRange',
  'damage', 'fireRate', 'weaponRange', 'projectileSpeed',
  'explosionDamage', 'explosionRadius',
  'repairRate', 'rechargeRate', 'supportRange', 'cooldown',
  'offsetX', 'offsetY', 'defaultRotation', 'scaleX', 'scaleY',
];

type OverrideMap = Record<string, ComponentOverride>;

const STORAGE_KEY = 'robogame_comp_overrides';

// ── Snapshot of original defaults (taken once at module load) ──

type OrigSnapshot = Pick<ComponentDef,
  'allowedEdges' | 'forbiddenEdges' | 'cost' | 'unlockCost' | 'hp' | 'mass' | 'energy' | 'force' | 'sensorRange' |
  'damage' | 'fireRate' | 'weaponRange' | 'projectileSpeed' |
  'explosionDamage' | 'explosionRadius' |
  'repairRate' | 'rechargeRate' | 'supportRange' | 'cooldown' |
  'offsetX' | 'offsetY' | 'defaultRotation' | 'scaleX' | 'scaleY' |
  'flipX' | 'flipY' | 'gridClickMode'
>;

const ORIGINAL_DEFS: Record<string, OrigSnapshot> = {};
for (const [key, def] of Object.entries(COMPONENT_DEFS)) {
  ORIGINAL_DEFS[key] = {
    allowedEdges: [...def.allowedEdges] as [boolean, boolean, boolean, boolean],
    forbiddenEdges: [...def.forbiddenEdges] as [boolean, boolean, boolean, boolean],
    cost: def.cost, unlockCost: def.unlockCost, hp: def.hp, mass: def.mass, energy: def.energy,
    force: def.force, sensorRange: def.sensorRange,
    damage: def.damage, fireRate: def.fireRate, weaponRange: def.weaponRange,
    projectileSpeed: def.projectileSpeed,
    explosionDamage: def.explosionDamage, explosionRadius: def.explosionRadius,
    repairRate: def.repairRate, rechargeRate: def.rechargeRate, supportRange: def.supportRange,
    cooldown: def.cooldown,
    offsetX: def.offsetX, offsetY: def.offsetY,
    defaultRotation: def.defaultRotation, scaleX: def.scaleX, scaleY: def.scaleY,
    flipX: def.flipX, flipY: def.flipY, gridClickMode: def.gridClickMode,
  };
}

// ── Mutable override map ──

let OVERRIDES: OverrideMap = {};

// Cached parse of localStorage for getOverride() — avoids repeated JSON.parse
let _lsCache: OverrideMap | null = null;

/** Invalidate the localStorage cache (call after any write to localStorage) */
function invalidateLsCache(): void { _lsCache = null; }

/** Read and cache the full override map from localStorage */
function getLsOverrides(): OverrideMap {
  if (_lsCache) return _lsCache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) { _lsCache = JSON.parse(raw) as OverrideMap; return _lsCache; }
  } catch (e) { console.warn('[ComponentOverrides] getLsOverrides parse failed:', e); }
  return {};
}

export function overrideKey(type: ComponentType, level: number): string {
  return `${type}_${level}`;
}

/** Return the original (pre-override) default value for a field on a component type.
 *  Use this instead of COMPONENT_DEFS when you need the un-mutated default. */
export function getOriginalDefault(type: ComponentType, field: string): any {
  const orig = ORIGINAL_DEFS[type];
  if (!orig) return undefined;
  return (orig as any)[field];
}

/** Return the evolved default value for a field at a given evolution level.
 *  Level 0 returns the original default.
 *  Level 1+ applies the statMultiplier from the evolution chain. */
export function getEvolvedDefault(type: ComponentType, level: number, field: string): any {
  const orig = ORIGINAL_DEFS[type];
  if (!orig) return undefined;

  // Level 0: just return the original default
  if (level === 0) return (orig as any)[field];

  const baseDef = COMPONENT_DEFS[type];

  // Cost: use costPerLevel array
  if (field === 'cost') {
    const clampedLevel = Math.min(level, 2);
    return baseDef.costPerLevel[clampedLevel];
  }

  // unlockCost: only meaningful at level 0
  if (field === 'unlockCost') return orig.unlockCost;

  // Visual fields: no evolution scaling
  if (['offsetX', 'offsetY', 'defaultRotation', 'scaleX', 'scaleY'].includes(field)) {
    return (orig as any)[field];
  }

  // Get evolution multiplier
  const chain = EVOLUTION_MAP.get(type);
  const stage = chain?.stages.find(s => s.level === level);
  const m = stage?.statMultiplier ?? getEvolutionMultiplier(level);
  const fm = 1 + (m - 1) * GOD.evoFireRateScale;

  const isFlight = type === ComponentType.WING || type === ComponentType.TURBINE;
  const isSupport = baseDef.category === ComponentCategory.SUPPORT;
  const supportRangeMult = isSupport ? Math.pow(GOD.evoSupportRangeBase, level) : 1;
  const drainReduction = isSupport ? Math.pow(GOD.evoSupportDrainBase, level) : 1;

  const base = (orig as any)[field] as number;
  if (base === undefined || base === null) return base;

  switch (field) {
    case 'hp':
      return isFlight ? Math.round(base / m) : Math.round(base * m);
    case 'energy':
      if (base > 0) return Math.round(base * m);
      if (isFlight) return +(base / m).toFixed(1);
      if (isSupport) return +(base * drainReduction).toFixed(1);
      return +(base * (1 + GOD.tierEnergyDrainMult * level)).toFixed(1);
    case 'damage': return +(base * m).toFixed(1);
    case 'fireRate': return +(base * fm).toFixed(2);
    case 'weaponRange': return Math.round(base * m);
    case 'sensorRange': return Math.round(base * m);
    case 'repairRate': return +(base * m).toFixed(1);
    case 'rechargeRate': return +(base * m).toFixed(1);
    case 'supportRange': return Math.round(base * supportRangeMult);
    case 'explosionDamage': return Math.round(base * m);
    case 'explosionRadius': return Math.round(base * m);
    case 'force': return isFlight ? Math.round(base * m) : base;
    case 'mass': return isFlight ? Math.round(base / m) : Math.round(base * (1 + GOD.tierMassMult * level));
    default: return (orig as any)[field];
  }
}

/** Return a shallow copy of the raw override map (for profile export) */
export function getOverridesSnapshot(): OverrideMap {
  return JSON.parse(JSON.stringify(OVERRIDES));
}

/** Import an entire override map (from profile import), apply + save */
export function importOverridesSnapshot(map: OverrideMap): void {
  OVERRIDES = map;
  applyAllLevel0();
  saveComponentOverrides();
}

// ── Persistence ──

export function saveComponentOverrides(): void {
  try {
    // Merge: preserve entries written by external tools (e.g. weapon tuner)
    // that may not be in the in-memory OVERRIDES cache
    let merged: OverrideMap = {};
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try { merged = JSON.parse(raw) as OverrideMap; } catch (e) { console.warn('[ComponentOverrides] merge parse failed:', e); }
    }
    // In-memory OVERRIDES take precedence
    Object.assign(merged, OVERRIDES);
    // Remove entries deleted in-memory (value explicitly set to empty)
    for (const key of Object.keys(merged)) {
      if (key in OVERRIDES && OVERRIDES[key] === undefined) {
        delete merged[key];
      }
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    invalidateLsCache();
  } catch (e) { console.warn('[ComponentOverrides] save failed:', e); }
  notifyProfileChanged();
}

export function loadComponentOverrides(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    OVERRIDES = JSON.parse(raw) as OverrideMap;
    invalidateLsCache();
    applyAllLevel0();
  } catch (e) { console.warn('[ComponentOverrides] load failed:', e); }
}

/** Get the current override for a type+level (or empty object if none).
 *  For level > 0, reads from cached localStorage to pick up Weapon Tuner changes.
 *  Cache is invalidated on save/load/import. */
export function getOverride(type: ComponentType, level: number): ComponentOverride {
  const key = overrideKey(type, level);
  if (level > 0) {
    const live = getLsOverrides();
    return { ...(live[key] ?? {}) };
  }
  return { ...(OVERRIDES[key] ?? {}) };
}

/** Set an override for a type+level. Applies to COMPONENT_DEFS if level=0 and saves. */
export function setOverride(type: ComponentType, level: number, override: ComponentOverride): void {
  const key = overrideKey(type, level);
  const orig = ORIGINAL_DEFS[type];
  if (!orig) return;

  // Only store non-default values (delta compression)
  const clean: ComponentOverride = {};

  // Edges
  if (override.allowedEdges) {
    const a = override.allowedEdges, o = orig.allowedEdges;
    if (a[0] !== o[0] || a[1] !== o[1] || a[2] !== o[2] || a[3] !== o[3]) {
      clean.allowedEdges = [...a] as [boolean, boolean, boolean, boolean];
    }
  }
  if (override.forbiddenEdges) {
    const a = override.forbiddenEdges, o = orig.forbiddenEdges;
    if (a[0] !== o[0] || a[1] !== o[1] || a[2] !== o[2] || a[3] !== o[3]) {
      clean.forbiddenEdges = [...a] as [boolean, boolean, boolean, boolean];
    }
  }

  // All numeric fields — compare against evolved defaults for level > 0
  for (const f of NUMERIC_FIELDS) {
    const val = override[f] as number | undefined;
    const defaultVal = level > 0 ? getEvolvedDefault(type, level, f as string) : (orig as any)[f];
    if (val !== undefined && val !== defaultVal) {
      (clean as any)[f] = val;
    }
  }

  // Boolean flip fields
  if (override.flipX !== undefined && override.flipX !== orig.flipX) clean.flipX = override.flipX;
  if (override.flipY !== undefined && override.flipY !== orig.flipY) clean.flipY = override.flipY;
  // Grid click mode
  if (override.gridClickMode !== undefined && override.gridClickMode !== orig.gridClickMode) clean.gridClickMode = override.gridClickMode;

  if (Object.keys(clean).length > 0) {
    OVERRIDES[key] = clean;
  } else {
    delete OVERRIDES[key];
  }

  if (level === 0) {
    const def = COMPONENT_DEFS[type];
    if (def) { restoreDef(def, type); if (OVERRIDES[key]) applyOverrideToDef(def, OVERRIDES[key]); }
  }

  saveComponentOverrides();
}

/** Clear all overrides and restore COMPONENT_DEFS to originals */
export function resetComponentOverrides(): void {
  OVERRIDES = {};
  for (const type of Object.keys(ORIGINAL_DEFS)) {
    const def = COMPONENT_DEFS[type as ComponentType];
    if (def) restoreDef(def, type as ComponentType);
  }
  saveComponentOverrides();
}

// ── Helpers ──

function applyAllLevel0(): void {
  for (const [key, override] of Object.entries(OVERRIDES)) {
    if (!key.endsWith('_0')) continue;
    const type = key.slice(0, -2) as ComponentType;
    const def = COMPONENT_DEFS[type];
    if (def) applyOverrideToDef(def, override);
  }
}

function applyOverrideToDef(def: ComponentDef, ov: ComponentOverride): void {
  if (ov.allowedEdges) def.allowedEdges = [...ov.allowedEdges] as [boolean, boolean, boolean, boolean];
  if (ov.forbiddenEdges) def.forbiddenEdges = [...ov.forbiddenEdges] as [boolean, boolean, boolean, boolean];
  for (const f of NUMERIC_FIELDS) {
    const val = ov[f] as number | undefined;
    if (val !== undefined) (def as any)[f] = val;
  }
  if (ov.flipX !== undefined) def.flipX = ov.flipX;
  if (ov.flipY !== undefined) def.flipY = ov.flipY;
  if (ov.gridClickMode !== undefined) def.gridClickMode = ov.gridClickMode;
}

function restoreDef(def: ComponentDef, type: ComponentType): void {
  const orig = ORIGINAL_DEFS[type];
  if (!orig) return;
  def.allowedEdges = [...orig.allowedEdges] as [boolean, boolean, boolean, boolean];
  def.forbiddenEdges = [...orig.forbiddenEdges] as [boolean, boolean, boolean, boolean];
  for (const f of NUMERIC_FIELDS) {
    (def as any)[f] = (orig as any)[f];
  }
  def.flipX = orig.flipX;
  def.flipY = orig.flipY;
  def.gridClickMode = orig.gridClickMode;
}
